import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { fmtPhone } from "../lib/time";
import {
  Loader2, CheckCircle, AlertCircle, Phone, Mail, MapPin, User, FileText, Send
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReferralForm() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "",
    notes: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/portal/referral/${code}`);
        setInfo(res.data);
        if (res.data.service_types?.length > 0) {
          setForm(f => ({ ...f, service_type: res.data.service_types[0].value }));
        }
      } catch (err) {
        setError(err.response?.status === 404 ? "This referral link is invalid." : "Failed to load form.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your name"); return; }
    if (!form.phone.trim() && !form.email.trim()) { toast.error("Please enter your phone or email"); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/public/portal/referral/${code}`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Link Not Found</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="referral-success">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Thank You!</h2>
          <p className="text-sm text-slate-500 mb-1">Your information has been submitted successfully.</p>
          <p className="text-sm text-slate-500">A member of the <strong>{info.company.name}</strong> team will be in touch shortly.</p>
          {info.company.phone && (
            <p className="text-sm text-slate-400 mt-4">Or call us at <a href={`tel:${info.company.phone}`} className="text-[#0F4C81] font-medium">{info.company.phone}</a></p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8" data-testid="referral-form-page">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#0F4C81] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Send className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">{info.company.name}</h1>
          <p className="text-sm text-slate-500 mt-2">
            <strong className="text-[#0F4C81]">{info.referrer_name}</strong> thought you might be interested in our services
          </p>
        </div>

        {/* Form */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Get a Free Estimate</h2>
            <p className="text-xs text-slate-500 mb-6">Fill out the form below and we'll get back to you quickly.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm text-slate-700">Full Name *</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    placeholder="John Smith"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="pl-10"
                    data-testid="referral-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-sm text-slate-700">Phone</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      placeholder="(555) 123-4567"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: fmtPhone(e.target.value) }))}
                      className="pl-10"
                      data-testid="referral-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm text-slate-700">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@email.com"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="pl-10"
                      data-testid="referral-email-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="text-sm text-slate-700">Property Address</Label>
                <div className="relative mt-1.5">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="address"
                    placeholder="123 Main St, City, SC"
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    className="pl-10"
                    data-testid="referral-address-input"
                  />
                </div>
              </div>

              {info.service_types?.length > 0 && (
                <div>
                  <Label htmlFor="service" className="text-sm text-slate-700">Service Needed</Label>
                  <select
                    id="service"
                    value={form.service_type}
                    onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}
                    className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20 focus:border-[#0F4C81]"
                    data-testid="referral-service-select"
                  >
                    {info.service_types.map(st => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label htmlFor="notes" className="text-sm text-slate-700">Additional Notes</Label>
                <textarea
                  id="notes"
                  rows={3}
                  placeholder="Tell us a bit about what you need..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20 focus:border-[#0F4C81] resize-none"
                  data-testid="referral-notes-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0F4C81] hover:bg-[#0d3f6a] text-white py-2.5"
                data-testid="referral-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {submitting ? "Submitting..." : "Request Free Estimate"}
              </Button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-4">
              Referred by {info.referrer_name}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
